import { HttpClient, HttpResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, EMPTY, forkJoin, from, map, mergeMap, Observable, of, retry, switchMap, tap, toArray } from 'rxjs';
import { LanguageCode } from '@shs-digi-care-monorepo/interfaces';
import { environment } from '../../../environments/environment';
import { FlatTranslations, TranslationTree } from '../models/translation-editor.model';
import { TranslationEditorOperations } from '../operations/translation-editor.operations';
import { TRANSLATION_SERVICE_NAME } from './translation-editor.tokens';

export type SignedUrlResponse = {
    signedURL: string;
};

export type SaveProgress = {
    completed: number;
    total: number;
    currentLanguage: string;
};

export type TranslateInput = {
    from: LanguageCode;
    to: LanguageCode;
    key: string;
    value: string;
};

type TranslatedTerm = {
    sourceTerm: string;
    translatedTerm: string;
};

type TranslateTermsResponse = {
    translations: TranslatedTerm[];
};

/** Maximum number of terms the translate endpoint accepts per request. */
const BATCH_SIZE = 20;
/** Number of translate requests kept in flight at once. */
const PARALLEL_REQUESTS = 10;
/** Retries for a transiently failing translate request. */
const MAX_RETRIES = 3;
/** Delay (ms) between translate retries. */
const RETRY_DELAY_MS = 600;

/**
 * Service that reads and writes translation files through the customer API gateway.
 *
 * Translation files live in S3 under `translations/{serviceName}/{language}.json`.
 * The gateway never proxies the file content itself – instead it issues short-lived
 * pre-signed S3 URLs that the browser uses to download / upload directly. Optimistic
 * concurrency on save is provided through the file's ETag (`If-Match`).
 */
@Injectable({ providedIn: 'root' })
export class TranslationEditorService {
    private readonly http = inject(HttpClient);
    private readonly serviceName = inject(TRANSLATION_SERVICE_NAME);
    private readonly apiUrl = `${environment.apiUrl}/translations`;

    /**
     * Translate the supplied terms to their requested target languages.
     *
     * The gateway translates one target language per call and accepts at most
     * {@link BATCH_SIZE} terms, so inputs are grouped by `to` language and chunked
     * before being dispatched in parallel. The returned array is ordered to match the
     * order of `input`, regardless of how the requests were batched.
     */
    public translate(input: TranslateInput[]): Observable<string[]> {
        if (input.length === 0) {
            return of([]);
        }

        const groups = new Map<LanguageCode, { index: number; value: string }[]>();
        input.forEach((item, index) => {
            const group = groups.get(item.to) ?? [];
            group.push({ index, value: item.value });
            groups.set(item.to, group);
        });

        const tasks: { language: LanguageCode; indices: number[]; terms: string[] }[] = [];
        for (const [language, items] of groups) {
            for (let i = 0; i < items.length; i += BATCH_SIZE) {
                const slice = items.slice(i, i + BATCH_SIZE);
                tasks.push({
                    language,
                    indices: slice.map((entry) => entry.index),
                    terms: slice.map((entry) => entry.value)
                });
            }
        }

        const ordered = new Array<string>(input.length);
        return from(tasks).pipe(
            mergeMap(
                (task) =>
                    this.translateBatch(task.language, task.terms).pipe(
                        map((translated) => ({ indices: task.indices, translated }))
                    ),
                PARALLEL_REQUESTS
            ),
            tap(({ indices, translated }) =>
                indices.forEach((targetIndex, position) => {
                    ordered[targetIndex] = translated[position] ?? input[targetIndex].value;
                })
            ),
            toArray(),
            map(() => ordered)
        );
    }

    /**
     * Lists the language codes that currently have a translation file for the configured service.
     */
    public availableLanguages(): Observable<string[]> {
        return this.http
            .get<string[]>(`${this.apiUrl}/languages`, { params: { serviceName: this.serviceName } })
            .pipe(catchError(() => of<string[]>([])));
    }

    /**
     * Load a translation file in two steps:
     *   1. Ask the gateway for a short-lived pre-signed download URL.
     *   2. Fetch the JSON directly from S3 through that URL.
     *
     * Failures (e.g. the language has no file yet) resolve to an empty tree so the editor
     * can still present the key set and let the user fill the language in.
     */
    public load(language: string): Observable<TranslationTree> {
        return this.loadWithMeta(language).pipe(map(({ tree }) => tree));
    }

    /**
     * Load the canonical translation set from `keys.json`. This file is the single source
     * of truth for which keys exist, and the English text it carries is the source content
     * used to seed the source language wherever a per-language file is incomplete. Actual
     * per-language values are loaded from the translation files via {@link load}.
     */
    public loadCanonical(): Observable<FlatTranslations> {
        return this.http.get<TranslationTree>(`/assets/i18n/keys.json`).pipe(
            map((tree) => TranslationEditorOperations.flatten(tree)),
            catchError(() => of<FlatTranslations>({}))
        );
    }

    public loadAll(languages: string[]): Observable<Record<string, TranslationTree>> {
        if (languages.length === 0) {
            return of({});
        }
        return forkJoin(
            languages.reduce<Record<string, Observable<TranslationTree>>>((acc, lang) => {
                acc[lang] = this.load(lang);
                return acc;
            }, {})
        );
    }

    /**
     * Persist a translation file using a read-modify-write merge so that edits made by
     * other people while the editor was open are not clobbered:
     *   1. Re-fetch the *current* remote file (it may have changed since page load) along
     *      with its ETag.
     *   2. Overlay only the keys this editor actually changed (the `changes` diff).
     *   3. Ask the gateway for a short-lived signed upload URL, passing the ETag as
     *      `If-Match` so the gateway rejects the upload if the file moved on in the meantime.
     *   4. PUT the merged JSON directly to that S3 URL.
     *
     * Only keys present in `changes` win; every untouched key keeps whatever value the
     * remote file currently holds.
     */
    public save(language: string, changes: FlatTranslations): Observable<void> {
        return this.loadWithMeta(language).pipe(
            switchMap(({ tree, etag }) => {
                const merged = TranslationEditorOperations.applyChanges(tree, changes);
                const json = JSON.stringify(merged, null, 4) + '\n';
                const blob = new Blob([json], { type: 'application/json' });
                return this.requestUploadUrl(language, etag).pipe(
                    switchMap(({ signedURL }) => this.uploadToSignedUrl(signedURL, blob))
                );
            }),
            map(() => undefined)
        );
    }

    /**
     * Persist the changed keys for multiple languages. Each language file is independently
     * re-fetched and merged, so concurrent edits to other files are preserved. The outer
     * observable completes once every per-language save resolves and fails if any of them fail.
     */
    public saveAll(changes: Record<string, FlatTranslations>): Observable<void> {
        const entries = Object.entries(changes);
        if (entries.length === 0) {
            return of(undefined);
        }
        return forkJoin(entries.map(([language, diff]) => this.save(language, diff))).pipe(map(() => undefined));
    }

    /**
     * Persist the changed keys for multiple languages with progress tracking.
     * Each language file is independently re-fetched and merged, so concurrent edits to other files
     * are preserved. Saves run in parallel (like {@link saveAll}); the observable emits a
     * {@link SaveProgress} update as each language finishes and completes once all have resolved.
     */
    public saveAllWithProgress(changes: Record<string, FlatTranslations>): Observable<SaveProgress> {
        const entries = Object.entries(changes);
        const total = entries.length;
        if (total === 0) {
            return EMPTY;
        }
        let completed = 0;
        return from(entries).pipe(
            mergeMap(([language, diff]) =>
                this.save(language, diff).pipe(
                    map(() => {
                        completed++;
                        return { completed, total, currentLanguage: language } satisfies SaveProgress;
                    })
                )
            )
        );
    }

    /**
     * Fetch a translation file together with its ETag (used for optimistic concurrency on save).
     * Resolves to an empty tree with no ETag when the file does not exist yet or the download fails.
     */
    private loadWithMeta(language: string): Observable<{ tree: TranslationTree; etag?: string }> {
        return this.requestDownloadUrl(language).pipe(
            switchMap(({ signedURL }) =>
                this.http
                    .get<TranslationTree>(signedURL, { observe: 'response' })
                    .pipe(map((response) => this.toTreeWithMeta(response)))
            ),
            catchError(() => of({ tree: {} as TranslationTree, etag: undefined }))
        );
    }

    private toTreeWithMeta(response: HttpResponse<TranslationTree>): { tree: TranslationTree; etag?: string } {
        const etag = response.headers.get('ETag') ?? undefined;
        return { tree: response.body ?? ({} as TranslationTree), etag };
    }

    private requestDownloadUrl(language: string): Observable<SignedUrlResponse> {
        return this.http.get<SignedUrlResponse>(`${this.apiUrl}/links/download`, {
            params: { serviceName: this.serviceName, language }
        });
    }

    private requestUploadUrl(language: string, etag?: string): Observable<SignedUrlResponse> {
        const params: Record<string, string> = { serviceName: this.serviceName, language };
        if (etag) {
            params['ifMatch'] = etag;
        }
        return this.http.get<SignedUrlResponse>(`${this.apiUrl}/links/upload`, { params });
    }

    private uploadToSignedUrl(url: string, blob: Blob): Observable<void> {
        return this.http.put<void>(url, blob, { headers: { 'Content-Type': 'application/json' } });
    }

    private translateBatch(language: LanguageCode, terms: string[]): Observable<string[]> {
        return this.http
            .post<TranslateTermsResponse>(`${this.apiUrl}/translate`, { terms }, { params: { language } })
            .pipe(
                map((response) => response.translations.map((entry) => entry.translatedTerm)),
                retry({ count: MAX_RETRIES, delay: RETRY_DELAY_MS })
            );
    }
}
